import React, { useCallback, useEffect, useState } from 'react'
import Module from '../../../../layout/Module'
import { Box, Chip } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import FormCurriculumElements from '../FormCurriculumElements/FormCurriculumElements'
import FormLcElements from '../FormLcElements/FormLcElements'
import FormLevels from '../FormLevels/FormLevels'
import useLearningContinuum from '../../../../../hooks/learning-continuum/useLearningContinuum'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import { updateLearningContinuumFormField } from '../../../../../store/actions/learning-continuum.actions'
import { handlerErrorV2 } from '../../../../../utils/errors'
import { Alert } from '@mui/material'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import DialogAction from '../Dialog/Dialog'
import Dialog from '../../../../dialog/Dialog'
import FormPreview from '../FormPreview/FormPreview'
import { validateCurriculumElements, validateLcElements, validateLevels } from '../../utils'
import isEmpty from 'lodash/isEmpty'
import { toggleAlertModule } from '../../../../../store/actions/alert.actions'
import LevelsElements from '../LevelsElements/LevelsElements'
import DialogLevelsElements from '../LevelsElements/DialogLevelsElements'

const Edit = () => {
  const stepsAllowed = ['curriculum-elements', 'lc-elements', 'levels', 'levels-elements'];
  const stepsSavedAllowed = ['curriculum-elements', 'lc-elements', 'levels'];
  const dispatch = useDispatch()
  const { updateLearningContinua, getLearningContinua, isLoading } = useLearningContinuum()
  const formData = useSelector((state) => state?.lc?.form || {})
  const permission = useSelector((state) => state?.session?.permissions?.lc || {})
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})
  const alertSave = useSelector(state => state?.alert?.save || {})

  const { curriculumId, id, step } = useParams()
  const [error, setError] = useState(null)
  const [show, setShow] = useState(false)
  const [modal, setModal] = useState(null)
  const [modal2, setModal2] = useState(null);
  
  const navigate = useNavigate()

  const [loading, toggleLoading] = useState(false)
  const [isLoadingSave, toggleIsLoadingSave] = useState(false)

  const onCancel = () => {
    navigate(`/dashboard/learning-continuum/${curriculumId}`)
  }
  const onChangeAlert = (key, value) => {
    dispatch(updateLearningContinuumFormField(key, value))
    if (!alertSave.mode || !alertSave.onSave) {
      dispatch(toggleAlertModule(false, 'Learning Continuum', 'edit', onSave, alertSave.onDontSave))
    }
  }

  const onSave = async (e) => {
    setError(null)
    e.preventDefault()
    try {
      let type = ''
      let errors = {}
      if (step === 'curriculum-elements') {
        type = 'curriculum-elements'
        errors = validateCurriculumElements(formData.curriculumElements, currentCurriculum)
      }
      if (step === 'lc-elements') {
        type = 'lc-elements'
        errors = validateLcElements(formData.lcElements, currentCurriculum.lcElements.children)
      }
      if (step === 'levels') {
        type = 'level'
        errors = validateLevels(formData.levels, currentCurriculum.levels)
      }

      if (!isEmpty(errors)) {
        setError(errors)
        return
      }

      toggleIsLoadingSave(true)
      let result = await updateLearningContinua(id, type, formData)
      if (result.error) {
        setError(result)
      }
    } finally {
      toggleIsLoadingSave(false)
    }
  }

  const onLoad = useCallback(async () => {
    if (id) {
      toggleLoading(true)
      try {
        const response = await getLearningContinua(curriculumId, id)
        if (response.error) {
          setError(response)
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        setError(message)
      } finally {
        toggleLoading(false)
      }
    }
  }, [curriculumId, id])

  useEffect(() => {
    onLoad()
  }, [onLoad])

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary flex justify-between'>
        <div>
          {formData && <Chip label={`v${formData.version || '1'}`}  color='primary' className='font-bold'/>}
        </div>
        <div className='flex items-center justify-end gap-2'>
          { stepsAllowed.includes(step) &&
            <>

              { permission.archived &&
                <IconTooltipButton
                  id={`archive-${id}`}
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Archive'
                  icon='archive'
                  style={{
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--destructive)',
                    color: '#fff',
                    padding: '0.45rem',
                  }}
                  className="shadow-sm"
                  onClick={() => setModal({ type: 'archive', form: formData })}
                />
              }
              { permission.deleted &&
                <IconTooltipButton
                  id={`delete-${id}`}
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Delete'
                  icon='trash-alt'
                  style={{
                    borderRadius: '0.5rem',
                    backgroundColor: '#fff',
                    color: 'var(--destructive)',
                    padding: '0.45rem',
                  }}
                  className="shadow-sm"
                  onClick={() => setModal({ type: 'delete', form: formData })}
                />
              }
              <div className='px-4' />
              { permission.preview &&
                <IconTooltipButton
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Preview'
                  icon='expand-alt'
                  style={{
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--primary)',
                    color: 'var(--primary-foreground)',
                    padding: '0.45rem',
                  }}
                  className="shadow-sm"
                  onClick={() => setShow(true)}
                />
              }
              { permission.cloned &&
                <IconTooltipButton
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Clone'
                  icon='clone'
                  color='secondary'
                  style={{
                    borderRadius: '0.5rem',
                    color: 'var(--primary)',
                    padding: '0.45rem',
                  }}
                  className="shadow-sm"
                  onClick={() => setModal({ type: 'clone', form: formData })}
                />
              }
              <div className='px-1' />
            </>
          }
          <AlertSaveButton
            submodule='Learning Continuum'
            disabled={loading || isLoadingSave || isLoading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm w-full'
            onDontSave={onCancel}
          />
          { permission.updated && stepsSavedAllowed.includes(step) &&
            <ButtonStartIcon
              disabled={loading || isLoadingSave || isLoading || error?.message}
              loading={isLoadingSave}
              onClick={onSave}
              startIcon={['fal','check']}
              color='primary'
              className='w-full'
            >
              Save
            </ButtonStartIcon>
          }
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer 
        loading={isLoadingSave || isLoading}
        classNameWrapper={isLoadingSave || isLoading || loading ?  '' : 'overflow-auto' }
      >
        { !loading && 
          <Box className={[
            'absolute inset-0',
            ['levels-elements'].includes(step) ? '' : 'pb-2 pt-6 px-4 ',
          ].join(' ')}>
            { error?.message &&
              <Alert
                severity='error' 
                className='mb-4'
                onClose={() => setError(null)}
              >
                {error?.message}
              </Alert> 
            }
            { stepsAllowed.includes(step) &&
              <>
              { step === 'curriculum-elements' && <FormCurriculumElements onChangeAlert={onChangeAlert} /> }
              { step === 'lc-elements' && <FormLcElements onChangeAlert={onChangeAlert} /> }
              { step === 'levels' && <FormLevels onChangeAlert={onChangeAlert} /> }
              { step === 'levels-elements' && <LevelsElements onChangeAlert={onChangeAlert} /> }
              </>
            }
          </Box>
        }
        { modal &&
          <DialogAction 
            show={!!modal} 
            onClose={() => setModal(null)} 
            type={modal.type} 
            formData={modal.form} 
          />
        }
        { modal2 &&
          <DialogLevelsElements
            open={!!modal2}
            onClose={() => setModal2(null)}
            formData={modal2.form}
            onSave={() => {}}
            type={`${modal2.type}`}
            element={modal2.element}
          />
        }
        { show &&
          <Dialog
            id={`user-preview-${id}`}
            show={show} 
            title="learning continua preview"
            onClose={() => setShow(false)}
            onCloseHeader={true}
            maxWidth='sm'
          >
            <FormPreview onElements={(type, element, form) => setModal2({ form, type, element })} />
          </Dialog>

        }
      </Module.BodyContainer>
    </>
  )
}

export default Edit